import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiMapPin, FiUsers, FiClock, FiCalendar, FiArrowLeft, FiBell, FiBellOff } from 'react-icons/fi';
import Navbar from '../components/Navbar';
import MatchChat from '../components/MatchChat';
import ConfirmDialog from '../components/ConfirmDialog';
import MatchStatusTimeline from '../components/MatchStatusTimeline';
import ApplicationModal from '../components/ApplicationModal';
import JoinRequestsManager from '../components/JoinRequestsManager';
import { matchAPI, notificationAPI } from '../services/api';
import { useAuthContext } from '../context/AuthContext';
import logger from '../utils/logger';

const MatchDetail = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user, token: authToken, isAuthenticated } = useAuthContext();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isMutingNotifications, setIsMutingNotifications] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [isSubmittingApplication, setIsSubmittingApplication] = useState(false);

  useEffect(() => {
    loadMatchDetails();
  }, [matchId]);

  const loadMatchDetails = async () => {
    try {
      setLoading(true);
      const response = await matchAPI.getMatchById(matchId);
      if (response.success) {
        setMatch(response.match);
        setError('');
      }
    } catch (err) {
      logger.error('Failed to load match details', { matchId, error: err.message });
      setError('Failed to load match details');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinClick = () => {
    if (!authToken) {
      logger.warn('Attempt to join match without authentication', { matchId });
      navigate('/login');
      return;
    }

    if (match.requiresApplication) {
      setShowApplicationModal(true);
    } else {
      handleJoinMatch();
    }
  };

  const handleApplicationSubmit = async (message, answers) => {
    setIsSubmittingApplication(true);
    try {
      await matchAPI.requestJoinMatch(matchId, message, answers);
      await loadMatchDetails();
      setShowApplicationModal(false);
    } catch (err) {
      logger.error('Failed to submit application', { matchId, error: err.message });
      setError(err.message || 'Failed to submit application');
    } finally {
      setIsSubmittingApplication(false);
    }
  };

  const handleJoinMatch = async () => {
    if (!authToken) {
      logger.warn('Attempt to join match without authentication', { matchId });
      navigate('/login');
      return;
    }

    setIsJoining(true);
    try {
      const result = await matchAPI.joinMatch(matchId);
      if (result.success && result.match) {
        setMatch(result.match);
      }
      // Reload match details to ensure full latest state
      await loadMatchDetails();
      logger.info('Successfully joined match', { matchId });
      setError('');
      // Trigger notification refresh in Navbar
      window.dispatchEvent(new Event('refreshNotifications'));
    } catch (err) {
      logger.error('Failed to join match', { matchId, error: err.message });
      setError(err.message);
    } finally {
      setIsJoining(false);
    }
  };

  const handleLeaveMatch = async () => {
    setIsLeaving(true);
    try {
      await matchAPI.leaveMatch(matchId);
      // Reload match details to update UI
      await loadMatchDetails();
      logger.info('Successfully left match', { matchId });
      setError('');
    } catch (err) {
      logger.error('Failed to leave match', { matchId, error: err.message });
      setError(err.message);
    } finally {
      setIsLeaving(false);
    }
  };

  const handleToggleMuteNotifications = async () => {
    setIsMutingNotifications(true);
    try {
      const result = await notificationAPI.toggleMuteMatch(matchId);
      setIsMuted(result.isMuted);
      logger.info('Notification mute toggled', { matchId, isMuted: result.isMuted });
    } catch (err) {
      logger.error('Failed to toggle notification mute', { matchId, error: err.message });
      setError('Failed to toggle notification settings');
    } finally {
      setIsMutingNotifications(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-black min-h-screen">
        <Navbar />

        <div className="max-w-7xl mx-auto px-4 py-8">
          <p className="text-gray-400">Loading match details...</p>
        </div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="bg-black min-h-screen">
        <Navbar />

        <div className="max-w-7xl mx-auto px-4 py-8">
          <p className="text-red-500">Match not found</p>
        </div>
      </div>
    );
  }

  const getId = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return value._id?.toString() || value.toString();
    return null;
  };

  const currentUserId = getId(user._id);
  const isUserParticipant = match.participants.some((p) => {
    const participantId = getId(p._id || p);
    return participantId === currentUserId;
  });

  const isUserCreator = getId(match.createdBy?._id || match.createdBy) === currentUserId;
  const spotsLeft = match.maxPlayers - match.participants.length;

  const hasPendingRequest = match.joinRequests?.some(
    r => (getId(r.user?._id || r.user) === currentUserId) && r.status === 'pending'
  );

  return (
    <div className="bg-black min-h-screen">
      <Navbar />


      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/discover')}
          className="flex items-center gap-2 text-red-500 hover:text-red-400 mb-8 transition"
        >
          <FiArrowLeft size={20} />
          Back to Discover
        </button>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-3 rounded mb-8">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Match Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div className="bg-gray-900 border-l-4 border-red-500 rounded-sm overflow-hidden">
              {match.matchImage && (
                <div className="w-full h-64">
                  <img src={match.matchImage} alt={match.title} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-8">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h1 className="text-4xl font-bold text-white mb-2">{match.title}</h1>
                    <p className="text-gray-400">{match.description}</p>
                  </div>
                  <div className="bg-red-500 text-white px-4 py-2 rounded text-sm font-bold">
                    {spotsLeft <= 0 ? 'FULL' : `${spotsLeft} SPOTS LEFT`}
                  </div>
                </div>

              {/* Match Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 pt-8 border-t border-gray-800">
                <div>
                  <p className="text-gray-500 text-sm uppercase tracking-wide mb-2">Sport</p>
                  <p className="text-white font-semibold capitalize">{match.sport}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm uppercase tracking-wide mb-2">Skill Level</p>
                  <p className="text-white font-semibold capitalize">{match.skillLevel}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm uppercase tracking-wide mb-2">Players</p>
                  <p className="text-white font-semibold">
                    {match.participants.length}/{match.maxPlayers}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm uppercase tracking-wide mb-2">Duration</p>
                  <p className="text-white font-semibold">{match.duration} min</p>
                </div>
              </div>

              {/* Location and Date/Time */}
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <FiMapPin className="text-red-500" size={20} />
                  <div>
                    <p className="text-gray-500 text-sm uppercase tracking-wide">Location</p>
                    <p className="text-white font-semibold">{match.location}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FiCalendar className="text-red-500" size={20} />
                  <div>
                    <p className="text-gray-500 text-sm uppercase tracking-wide">Date</p>
                    <p className="text-white font-semibold">
                      {new Date(match.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FiClock className="text-red-500" size={20} />
                  <div>
                    <p className="text-gray-500 text-sm uppercase tracking-wide">Time</p>
                    <p className="text-white font-semibold">{match.time}</p>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              {authToken ? (
                <div className="pt-8 border-t border-gray-800">
                  {isUserCreator ? (
                    <p className="text-red-500 text-sm">You are the organizer of this match</p>
                  ) : isUserParticipant ? (
                    <div className="space-y-3">
                      <p className="text-green-400 text-sm font-semibold">
                        You have joined this match
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowLeaveConfirm(true)}
                        disabled={isLeaving}
                        className="w-full bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white font-bold py-3 rounded transition"
                      >
                        {isLeaving ? 'Leaving...' : 'Leave Match'}
                      </button>
                    </div>
                  ) : hasPendingRequest ? (
                    <button
                      type="button"
                      disabled
                      className="w-full bg-yellow-500/20 border border-yellow-500 text-yellow-500 font-bold py-3 rounded"
                    >
                      Application Pending
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleJoinClick}
                      disabled={isJoining || spotsLeft <= 0}
                      className="w-full bg-red-500 hover:bg-red-600 disabled:bg-gray-700 text-white font-bold py-3 rounded transition"
                    >
                      {isJoining
                        ? 'Joining...'
                        : spotsLeft <= 0
                        ? 'Match is Full'
                        : match.requiresApplication
                        ? 'Apply to Join'
                        : 'Join Match'}
                    </button>
                  )}

                  {/* Mute Notifications Button */}
                  <button
                    type="button"
                    onClick={handleToggleMuteNotifications}
                    disabled={isMutingNotifications}
                    className="w-full mt-3 flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white font-semibold py-2 rounded transition"
                  >
                    {isMuted ? (
                      <>
                        <FiBellOff size={16} />
                        {isMutingNotifications ? 'Unmuting...' : 'Notifications Muted'}
                      </>
                    ) : (
                      <>
                        <FiBell size={16} />
                        {isMutingNotifications ? 'Muting...' : 'Mute Notifications'}
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="pt-8 border-t border-gray-800 text-center">
                  <p className="text-gray-400 text-sm mb-4">Want to join this match?</p>
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-3 rounded transition"
                  >
                    LOGIN TO JOIN
                  </button>
                </div>
              )}

              </div>
            </div>

            {/* Join Requests Manager (Only for Organizer) */}
            {isUserCreator && match.requiresApplication && (
              <JoinRequestsManager 
                matchId={matchId} 
                requests={match.joinRequests} 
                onUpdate={loadMatchDetails} 
              />
            )}

            {/* Participants */}
            <div className="bg-gray-900 border-l-4 border-red-500 p-8 rounded-sm mt-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <FiUsers size={24} />
                Participants ({match.participants.length})
              </h2>
              <div className="space-y-3">
                {/* Organizer */}
                <div 
                  className="bg-gray-800 p-4 rounded cursor-pointer hover:bg-gray-700 transition"
                  onClick={() => navigate(`/profile/${getId(match.createdBy)}`)}
                >
                  <p className="text-white font-semibold hover:text-red-500 transition">{match.createdBy.fullName}</p>
                  <p className="text-gray-400 text-sm">Organizer</p>
                </div>

                {/* Other Participants */}
                {match.participants.map((participant) => {
                  const participantId = getId(participant._id || participant);
                  const isCurrentUser = participantId === currentUserId;
                  return (
                    <div 
                      key={participant._id || participantId} 
                      className="bg-gray-800 p-4 rounded cursor-pointer hover:bg-gray-700 transition"
                      onClick={() => navigate(`/profile/${participantId}`)}
                    >
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <p className="text-white font-semibold hover:text-red-500 transition">{participant.fullName || participant.email || 'Participant'}</p>
                        {isCurrentUser && (
                          <span className="text-green-400 text-xs uppercase tracking-wider font-semibold">
                            You
                          </span>
                        )}
                      </div>
                      <p className="text-gray-400 text-sm">{participant.email || ''}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Sidebar - Info & Actions */}
          <div className="lg:col-span-1 space-y-6">
            {/* Match Status Timeline */}
            <MatchStatusTimeline 
              status={match.status}
              date={match.date}
              startTime={match.time}
            />

            {/* Chat Sidebar */}
            {authToken && isUserParticipant && (
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Match Chat</h2>
                <div className="sticky top-20">
                  <MatchChat matchId={matchId} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Red Line */}
      <div className="h-1 bg-gradient-to-r from-red-500 via-red-500 to-transparent mt-16"></div>

      {/* Leave Match Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showLeaveConfirm}
        title="Leave Match?"
        message="Are you sure you want to leave this match? You can join again later."
        confirmText="Leave"
        cancelText="Cancel"
        isDestructive={true}
        isLoading={isLeaving}
        onConfirm={handleLeaveMatch}
        onCancel={() => setShowLeaveConfirm(false)}
      />

      {/* Application Modal */}
      <ApplicationModal
        isOpen={showApplicationModal}
        onClose={() => setShowApplicationModal(false)}
        onSubmit={handleApplicationSubmit}
        matchTitle={match.title}
        questions={match.applicationQuestions}
        isSubmitting={isSubmittingApplication}
      />
    </div>
  );
};

export default MatchDetail;
